import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  UserCheck, Clock, Calendar, Download, FileText,
  Music, CheckCircle, XCircle, Edit2, Plus,
  ChevronLeft, ChevronRight, Search, Loader2, Users, Table2, QrCode, LogIn, LogOut, Lock, KeyRound
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const CLOCKIN_URL = `${(typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || '')}/volunteer-clockin`;

const DAY_COLORS = {
  Sunday: 'bg-amber-100 text-amber-800 border-amber-300',
  Wednesday: 'bg-blue-100 text-blue-800 border-blue-300',
  Friday: 'bg-purple-100 text-purple-800 border-purple-300',
  Saturday: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  Thursday: 'bg-orange-100 text-orange-800 border-orange-300',
};

const SESSION_ICONS = { worship: Music, prayer: Clock, training: Users };

const VolunteerSection = () => {
  const [assignments, setAssignments] = useState([]);
  const [selectedVol, setSelectedVol] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [editingLog, setEditingLog] = useState(null);
  const [editForm, setEditForm] = useState({ notes: '', duration_hours: 0 });
  const [showQR, setShowQR] = useState(false);

  // Add volunteer form fields
  const [selectedMember, setSelectedMember] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [newDept, setNewDept] = useState('');

  // Password management
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordVolunteer, setPasswordVolunteer] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  // Report date range
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
  const [reportFrom, setReportFrom] = useState(thirtyDaysAgo);
  const [reportTo, setReportTo] = useState(today);

  const getWeekStart = useCallback((offset = 0) => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1 + offset * 7);
    return d.toISOString().split('T')[0];
  }, []);

  const getWeekEnd = useCallback((offset = 0) => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 7 + offset * 7);
    return d.toISOString().split('T')[0];
  }, []);

  useEffect(() => {
    axios.get(`${API}/volunteers/assignments`).then(r => {
      setAssignments(r.data || []);
      if (r.data?.length > 0) setSelectedVol(r.data[0]);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedVol) return;
    const ws = getWeekStart(weekOffset);
    const we = getWeekEnd(weekOffset);
    axios.get(`${API}/volunteers/sessions/${selectedVol.id}`).then(r => setSessions(r.data || []));
    axios.get(`${API}/volunteers/attendance/${selectedVol.id}?start_date=${ws}&end_date=${we}`).then(r => setAttendance(r.data || []));
  }, [selectedVol, weekOffset, getWeekStart, getWeekEnd]);

  const generateWeek = async () => {
    if (!selectedVol) return;
    const ws = getWeekStart(weekOffset);
    await axios.post(`${API}/volunteers/attendance/generate-week?volunteer_id=${selectedVol.id}&week_start=${ws}`);
    const we = getWeekEnd(weekOffset);
    const r = await axios.get(`${API}/volunteers/attendance/${selectedVol.id}?start_date=${ws}&end_date=${we}`);
    setAttendance(r.data || []);
  };

  const toggleStatus = async (log) => {
    const newStatus = log.status === 'present' ? 'absent' : 'present';
    await axios.put(`${API}/volunteers/attendance/${log.id}`, { status: newStatus });
    setAttendance(prev => prev.map(l => l.id === log.id ? { ...l, status: newStatus } : l));
  };

  const saveEdit = async () => {
    if (!editingLog) return;
    await axios.put(`${API}/volunteers/attendance/${editingLog.id}`, editForm);
    setAttendance(prev => prev.map(l => l.id === editingLog.id ? { ...l, ...editForm } : l));
    setEditingLog(null);
  };

  const loadCustomReport = async () => {
    if (!selectedVol || !reportFrom || !reportTo) return;
    const r = await axios.get(`${API}/volunteers/report/custom/${selectedVol.id}?start_date=${reportFrom}&end_date=${reportTo}`);
    setReport(r.data);
    setShowReport(true);
  };

  const downloadCustomPdf = () => {
    if (!selectedVol) return;
    window.open(`${API}/volunteers/report/custom/${selectedVol.id}/pdf?start_date=${reportFrom}&end_date=${reportTo}`, '_blank');
  };

  const downloadCustomExcel = () => {
    if (!selectedVol) return;
    window.open(`${API}/volunteers/report/custom/${selectedVol.id}/excel?start_date=${reportFrom}&end_date=${reportTo}`, '_blank');
  };

  const addVolunteer = async () => {
    if (!selectedMember || !newRole.trim() || !newDept.trim()) return;
    const r = await axios.post(`${API}/volunteers/assignments`, {
      member_id: selectedMember.id,
      member_name: selectedMember.full_name,
      role_title: newRole.trim(),
      department: newDept.trim()
    });
    if (r.data.success) {
      const updated = await axios.get(`${API}/volunteers/assignments`);
      setAssignments(updated.data || []);
      setSelectedVol(updated.data?.find(a => a.member_id === selectedMember.id) || updated.data?.[0]);
      resetAddForm();
    }
  };

  const resetAddForm = () => {
    setShowAdd(false);
    setMemberSearch('');
    setSearchResults([]);
    setSelectedMember(null);
    setNewRole('');
    setNewDept('');
  };

  const savePassword = async () => {
    if (!passwordVolunteer || !newPassword.trim()) return;
    setSavingPassword(true);
    try {
      await axios.put(`${API}/volunteers/password/${passwordVolunteer.id}`, { password: newPassword.trim() });
      setShowPasswordModal(false);
      setPasswordVolunteer(null);
      setNewPassword('');
    } catch (e) {
      alert('Failed to save password. Please try again.');
    }
    setSavingPassword(false);
  };

  const weekStart = getWeekStart(weekOffset);
  const weekEnd = getWeekEnd(weekOffset);
  const totalHours = attendance.filter(l => l.status === 'present').reduce((s, l) => s + l.duration_hours, 0);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="volunteer-section">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-slate-800">Volunteer Attendance & Reports</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowQR(!showQR)} data-testid="show-qr-btn"
            className="px-4 py-2 bg-slate-800 text-white rounded-xl font-medium hover:bg-slate-700 flex items-center gap-2">
            <QrCode className="w-4 h-4" /> Clock-In QR
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="add-volunteer-btn"
            className="px-4 py-2 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Volunteer
          </button>
        </div>
      </div>

      {/* QR Code Card */}
      <AnimatePresence>
        {showQR && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden">
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-2xl p-6 flex flex-col items-center" data-testid="qr-card">
              <p className="text-white/70 text-sm mb-4">Volunteers scan this QR code to clock in & out</p>
              <div className="p-4 bg-white rounded-2xl">
                <QRCodeSVG value={CLOCKIN_URL} size={180} level="H" />
              </div>
              <p className="text-amber-400 text-xs mt-4 font-mono break-all text-center">{CLOCKIN_URL}</p>
              <p className="text-white/40 text-xs mt-1 text-center">Print this or display on screen at the church entrance</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Volunteer selector */}
      {assignments.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {assignments.map(a => (
            <button key={a.id} onClick={() => setSelectedVol(a)}
              className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
                selectedVol?.id === a.id ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {a.member_name}
            </button>
          ))}
        </div>
      )}

      {selectedVol ? (
        <>
          {/* Profile Card */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-2xl p-5 text-white">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center">
                <UserCheck className="w-7 h-7 text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold">{selectedVol.member_name}</h3>
                <p className="text-white/70 text-sm">{selectedVol.role_title} | {selectedVol.department}</p>
                <span className="text-xs bg-emerald-500/30 text-emerald-300 px-2 py-0.5 rounded-full">{selectedVol.status}</span>
              </div>
              <div className="text-right flex flex-col items-end gap-2">
                <div>
                  <p className="text-3xl font-bold text-amber-400">{totalHours}h</p>
                  <p className="text-xs text-white/50">This week</p>
                </div>
                <button onClick={() => { setPasswordVolunteer(selectedVol); setNewPassword(''); setShowPasswordModal(true); }}
                  data-testid="set-password-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-medium transition-colors">
                  <KeyRound className="w-3.5 h-3.5 text-amber-400" /> Set Password
                </button>
              </div>
            </div>
          </div>

          {/* Schedule Overview */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <h4 className="font-semibold text-slate-700 mb-3">Weekly Ministry Schedule</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {sessions.map(s => {
                const Icon = SESSION_ICONS[s.session_type] || Clock;
                return (
                  <div key={s.id} className={`rounded-xl p-3 border ${DAY_COLORS[s.day_of_week] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-4 h-4" />
                      <span className="font-bold text-sm">{s.day_of_week}</span>
                    </div>
                    <p className="text-xs font-medium leading-tight">{s.name}</p>
                    <p className="text-xs mt-1 opacity-70">{s.duration_hours}h</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Week Navigation + Attendance */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <button onClick={() => setWeekOffset(w => w - 1)} className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <h4 className="font-semibold text-slate-700 text-sm">{weekStart} — {weekEnd}</h4>
                <button onClick={() => setWeekOffset(w => w + 1)} className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200">
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button onClick={() => setWeekOffset(0)} className="text-xs text-amber-600 hover:text-amber-700 font-medium ml-1">Today</button>
              </div>
              {attendance.length === 0 && (
                <button onClick={generateWeek} data-testid="generate-week-btn"
                  className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600">
                  Generate Week
                </button>
              )}
            </div>

            {attendance.length > 0 ? (
              <div className="space-y-2">
                {attendance.map(log => (
                  <div key={log.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                    log.status === 'present' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
                    data-testid={`attendance-log-${log.id}`}>
                    <button onClick={() => toggleStatus(log)} data-testid={`toggle-status-${log.id}`}
                      className={`p-2 rounded-lg ${log.status === 'present' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
                      {log.status === 'present' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                    </button>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-800 text-sm">{log.session_name}</p>
                      <p className="text-xs text-slate-500">{log.session_date} | {log.duration_hours}h | {log.status.charAt(0).toUpperCase() + log.status.slice(1)}</p>
                      {(log.clock_in_time || log.clock_out_time) && (
                        <div className="flex items-center gap-3 mt-1">
                          {log.clock_in_time && (
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <LogIn className="w-3 h-3" /> In: {log.clock_in_time}
                            </span>
                          )}
                          {log.clock_out_time && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <LogOut className="w-3 h-3" /> Out: {log.clock_out_time}
                            </span>
                          )}
                        </div>
                      )}
                      {log.notes && <p className="text-xs text-slate-400 italic mt-0.5">{log.notes}</p>}
                    </div>
                    <button onClick={() => { setEditingLog(log); setEditForm({ notes: log.notes || '', duration_hours: log.duration_hours }); }}
                      data-testid={`edit-log-${log.id}`}
                      className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500">
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <span className="text-sm text-slate-500">Total Engagement Time</span>
                  <span className="font-bold text-lg text-emerald-600">{totalHours} hours</span>
                </div>
              </div>
            ) : (
              <p className="text-center text-slate-400 py-6 text-sm">No attendance entries for this week. Click "Generate Week" to create them.</p>
            )}
          </div>

          {/* Reports — Custom Date Range */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <h4 className="font-semibold text-slate-700 mb-3">Ministry Reports</h4>

            {/* Date Range Picker */}
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">From</label>
                <input type="date" value={reportFrom} onChange={e => setReportFrom(e.target.value)}
                  data-testid="report-date-from"
                  className="border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-amber-400 outline-none" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">To</label>
                <input type="date" value={reportTo} onChange={e => setReportTo(e.target.value)}
                  data-testid="report-date-to"
                  className="border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-amber-400 outline-none" />
              </div>
              <button onClick={loadCustomReport} data-testid="view-custom-report"
                className="px-4 py-2.5 bg-blue-50 text-blue-700 rounded-lg font-medium text-sm hover:bg-blue-100 flex items-center gap-2">
                <FileText className="w-4 h-4" /> View Report
              </button>
            </div>

            {/* Download Buttons */}
            <div className="grid grid-cols-2 gap-2">
              <button onClick={downloadCustomPdf} data-testid="download-custom-pdf"
                className="py-3 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 flex items-center justify-center gap-2">
                <Download className="w-4 h-4" /> Download PDF
              </button>
              <button onClick={downloadCustomExcel} data-testid="download-custom-excel"
                className="py-3 bg-emerald-600 text-white rounded-xl font-medium text-sm hover:bg-emerald-700 flex items-center justify-center gap-2">
                <Table2 className="w-4 h-4" /> Download Excel
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
          <UserCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No volunteers registered yet.</p>
          <button onClick={() => setShowAdd(true)} className="mt-3 px-4 py-2 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600">
            Add Volunteer
          </button>
        </div>
      )}

      {/* Edit Modal */}
      <AnimatePresence>
        {editingLog && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditingLog(null)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="edit-modal">
              <h3 className="font-bold text-lg mb-4">Edit Attendance</h3>
              <p className="text-sm text-slate-500 mb-3">{editingLog.session_name} — {editingLog.session_date}</p>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Duration (hours)</label>
                  <input type="number" step="0.5" min="0" value={editForm.duration_hours}
                    onChange={e => setEditForm(prev => ({ ...prev, duration_hours: parseFloat(e.target.value) || 0 }))}
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 mt-1" data-testid="edit-duration" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Notes</label>
                  <textarea value={editForm.notes} rows={3}
                    onChange={e => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 mt-1" data-testid="edit-notes" />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditingLog(null)} className="flex-1 py-2.5 bg-slate-100 rounded-xl font-medium">Cancel</button>
                  <button onClick={saveEdit} data-testid="save-edit-btn"
                    className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600">Save</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Volunteer Modal */}
      <AnimatePresence>
        {showAdd && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={resetAddForm}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="add-volunteer-modal">
              <h3 className="font-bold text-lg mb-4">Add Volunteer</h3>

              {/* Step 1: Search & Select Member */}
              <div className="mb-4">
                <label className="text-sm font-medium text-slate-700 block mb-1">Select Member</label>
                {selectedMember ? (
                  <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                    <div>
                      <span className="font-bold text-slate-800">{selectedMember.full_name}</span>
                      <span className="text-xs text-slate-500 ml-2">{selectedMember.member_number || ''}</span>
                    </div>
                    <button onClick={() => { setSelectedMember(null); setMemberSearch(''); setSearchResults([]); }}
                      className="text-xs text-amber-600 font-medium hover:text-amber-700">Change</button>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="flex items-center border-2 border-slate-200 rounded-xl px-4 py-2.5">
                      <Search className="w-4 h-4 text-slate-400 mr-2" />
                      <input type="text" placeholder="Search member by name or ID..." value={memberSearch}
                        onChange={async (e) => {
                          setMemberSearch(e.target.value);
                          if (e.target.value.length >= 2) {
                            const r = await axios.get(`${API}/members/search/${encodeURIComponent(e.target.value)}`);
                            setSearchResults(r.data || []);
                          } else setSearchResults([]);
                        }}
                        className="flex-1 outline-none" data-testid="volunteer-member-search" />
                    </div>
                    {searchResults.length > 0 && (
                      <div className="mt-2 border border-slate-200 rounded-xl max-h-48 overflow-y-auto">
                        {searchResults.map(m => (
                          <button key={m.id} onClick={() => { setSelectedMember(m); setSearchResults([]); setMemberSearch(''); }}
                            className="w-full text-left px-4 py-2.5 hover:bg-amber-50 flex items-center justify-between border-b last:border-0"
                            data-testid={`add-vol-member-${m.id}`}>
                            <span className="font-medium">{m.full_name}</span>
                            <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full font-mono">{m.member_number || '—'}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Step 2: Role & Department */}
              <div className="space-y-3 mb-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Role / Title</label>
                  <input type="text" placeholder="e.g., Voluntary Drummer, Choir Lead..." value={newRole}
                    onChange={e => setNewRole(e.target.value)}
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-amber-400 outline-none"
                    data-testid="volunteer-role-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Department</label>
                  <input type="text" placeholder="e.g., Music Ministry, Choir Department..." value={newDept}
                    onChange={e => setNewDept(e.target.value)}
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-amber-400 outline-none"
                    data-testid="volunteer-dept-input" />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button onClick={resetAddForm} className="flex-1 py-2.5 bg-slate-100 rounded-xl font-medium">Cancel</button>
                <button onClick={addVolunteer} data-testid="confirm-add-volunteer-btn"
                  disabled={!selectedMember || !newRole.trim() || !newDept.trim()}
                  className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed">
                  Create Volunteer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Report Modal */}
      <AnimatePresence>
        {showReport && report && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowReport(false)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="report-modal">
              <h3 className="font-bold text-lg mb-1">Ministry Participation Report</h3>
              <p className="text-sm text-slate-500 mb-1">{report.volunteer_name} — {report.role}</p>
              <p className="text-xs text-slate-400 mb-4">{report.period_start} to {report.period_end}</p>

              <div className="bg-slate-50 rounded-xl p-4 mb-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Department:</span> <span className="font-medium">{report.department}</span></div>
                  <div><span className="text-slate-500">Status:</span> <span className="font-medium">{report.status}</span></div>
                  <div><span className="text-slate-500">Sessions:</span> <span className="font-bold text-emerald-600">{report.sessions_attended}/{report.total_sessions}</span></div>
                  <div><span className="text-slate-500">Engagement:</span> <span className="font-bold text-amber-600">{report.total_engagement_hours}h</span></div>
                </div>
              </div>

              <h4 className="font-semibold text-sm text-slate-700 mb-2">Session Details</h4>
              <div className="space-y-1 mb-4">
                {report.sessions?.map((s, i) => (
                  <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
                    s.status === 'present' ? 'bg-emerald-50' : 'bg-red-50'}`}>
                    <span>{s.session_date}</span>
                    <span className="font-medium">{s.session_name}</span>
                    <span>{s.duration_hours}h</span>
                    <span className={`font-bold ${s.status === 'present' ? 'text-emerald-600' : 'text-red-600'}`}>{s.status}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <button onClick={() => setShowReport(false)} className="flex-1 py-2.5 bg-slate-100 rounded-xl font-medium">Close</button>
                <button onClick={downloadCustomPdf} data-testid="report-download-pdf"
                  className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" /> PDF
                </button>
                <button onClick={downloadCustomExcel} data-testid="report-download-excel"
                  className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 flex items-center justify-center gap-2">
                  <Table2 className="w-4 h-4" /> Excel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Set Password Modal */}
      <AnimatePresence>
        {showPasswordModal && passwordVolunteer && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowPasswordModal(false)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()} data-testid="password-modal">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-amber-100"><KeyRound className="w-5 h-5 text-amber-600" /></div>
                <div>
                  <h3 className="font-bold text-lg">Set Clock-In Password</h3>
                  <p className="text-xs text-slate-500">{passwordVolunteer.member_name}</p>
                </div>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                This password will be required when the volunteer scans the QR code to clock in or out.
              </p>
              <input
                type="text"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Enter password..."
                data-testid="password-input"
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none mb-4"
                autoFocus
              />
              <div className="flex gap-2">
                <button onClick={() => { setShowPasswordModal(false); setPasswordVolunteer(null); }}
                  className="flex-1 py-2.5 bg-slate-100 rounded-xl font-medium">Cancel</button>
                <button onClick={savePassword} disabled={!newPassword.trim() || savingPassword}
                  data-testid="save-password-btn"
                  className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 disabled:opacity-40 flex items-center justify-center gap-2">
                  <Lock className="w-4 h-4" /> {savingPassword ? 'Saving...' : 'Save Password'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VolunteerSection;
